/**
 * Settings form helpers — PURE, unit-testable. Drive the local draft/dirty model
 * on the Settings page: flatten the server's sections into a flat key→value map,
 * diff a draft against the server baseline so only CHANGED keys are sent on save,
 * and normalise currency inputs to 2dp for display. The server owns validation;
 * this only shapes what the UI sends and shows.
 */
import type { SettingsSection } from '@/lib/schemas/settings'

/** Flatten sections into a flat { key: value } map of every field's value. */
export function settingsToValues(sections: SettingsSection[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const section of sections) {
    for (const field of section.settings) {
      out[field.key] = field.value
    }
  }
  return out
}

/** Keys whose draft value differs from the server baseline, with their new values. */
export function diffChanges(
  baseline: Record<string, string>,
  draft: Record<string, string>,
): Record<string, string> {
  const changes: Record<string, string> = {}
  for (const key of Object.keys(draft)) {
    if (baseline[key] !== draft[key]) changes[key] = draft[key]
  }
  return changes
}

/** True when the draft has any changed key vs the baseline. */
export function isDirty(
  baseline: Record<string, string>,
  draft: Record<string, string>,
): boolean {
  return Object.keys(diffChanges(baseline, draft)).length > 0
}

/**
 * Reconcile the local draft against a fresh server baseline WITHOUT clobbering
 * the user's unsaved edits. A field the user has changed away from the baseline
 * it was last synced from (`prevBaseline`) keeps its draft value (dirty wins);
 * every other field adopts the incoming server value. Used for background
 * refetches — a full replace is used instead right after the user's own save.
 */
export function reconcileDraft(
  prevBaseline: Record<string, string>,
  nextBaseline: Record<string, string>,
  draft: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = { ...nextBaseline }
  for (const key of Object.keys(draft)) {
    if (draft[key] !== prevBaseline[key]) out[key] = draft[key]
  }
  return out
}

/**
 * Normalise a currency input to 2dp ("125" → "125.00"). Blank stays blank;
 * non-numeric text is left untouched so the server can reject it with a message.
 */
export function normalizeCurrency(value: string): string {
  const trimmed = value.trim()
  if (trimmed === '') return ''
  const n = Number(trimmed)
  if (Number.isNaN(n)) return trimmed
  return n.toFixed(2)
}
